import React, { FC, useCallback, useMemo } from 'react';
import { useLocation, useParams } from 'react-router-dom';

import { SegmentedControl, SegmentedControlOptionProps, SegmentedControlVariant } from '@column/column-ui-kit';

import { useNavigate } from '~/lib/navigation';

interface Entry extends Partial<SegmentedControlOptionProps> {
  path: string;
  hide?: boolean;
  activePath?: string;
}

interface TabbedPageProps {
  entries?: Entry[];
  className?: string;
}

export const TabbedPage: FC<TabbedPageProps> = ({ className, entries }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();

  const handleNavChange = useCallback(
    (path: string) => {
      navigate(path);
    },
    [navigate]
  );

  const replaceParams = useCallback(
    (path: string) => {
      let newPath = path;
      Object.keys(params).forEach((key) => {
        const value = params[key];
        if (typeof value === 'string') {
          newPath = newPath.replace(`:${key}`, value);
        }
      });
      return newPath;
    },
    [params]
  );

  const options = useMemo(
    () =>
      entries
        ? (entries
            .map(
              (entry: Entry) =>
                !entry.hide && {
                  label: entry.label === 'Edit' ? 'Account Details' : entry.label,
                  value: replaceParams(entry.path),
                  removeWildcard: entry.removeWildcard,
                }
            )
            .filter(Boolean) as SegmentedControlOptionProps[])
        : [],
    [entries, replaceParams]
  );

  if (!entries) {
    return null;
  }

  return (
    <SegmentedControl
      className={className}
      active={location.pathname}
      activeWildcard
      options={options}
      onOptionChange={handleNavChange}
      variant={SegmentedControlVariant.Line}
      isResponsive
    />
  );
};
